import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import Slider, { Settings } from 'react-slick';
import cn from 'classnames';

import {
	InsuranceProgramFragment,
	SelectedInsuranceFragment,
	PassengerFragment,
	CheckinPassengerFragment
} from '@websky/graphql';
import AdditionalServiceWrapper from '../../AdditionalServiceWrapper';
import InsuranceCard from './InsuranceCard/InsuranceCard';
import { useTheme } from '../../theme';
import { Money } from '../../Money';
import { MOBILE_MIN_WIDTH } from '../../utils';
import { Chevron } from '../../Icons';
import { useInsuranceState } from '../hooks';
import { analytics_addInsurance, analytics_removeInsurance } from '../../Insurance/utils';
import WebskyAnalyticsApollo from '../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../DataLayerAnalytics/types';
import { SafeHtml } from '@shared/ui';

export interface Props {
	className?: string;
	header?: string;
	description?: string;
	travellers: (PassengerFragment | CheckinPassengerFragment)[];
	insurances: InsuranceProgramFragment[];
	selectedInsurances: SelectedInsuranceFragment[];
	onSave: (state: string[]) => void;
	onClose: () => void;
	isLoading?: boolean;
}

const InsurancePopup: React.FC<Props> = ({
	className,
	header,
	description,
	travellers,
	insurances,
	selectedInsurances,
	onClose,
	onSave,
	isLoading
}) => {
	const { InsurancePopup: css } = useTheme('InsurancePopup');
	const { t } = useTranslation('Checkout');

	const [slider, setSlider] = useState<Slider>();

	const { state, totalPrice, hasChanges, onToggle } = useInsuranceState(insurances, selectedInsurances);

	const oneInsurance = insurances.length === 1 ? insurances[0] : null;

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const isSmall = !isMobile && insurances.length < 3;

	const sliderOptions: Settings = {
		className: cn(css.slider, {
			[css.small]: isSmall
		}),
		slidesToShow: isSmall ? insurances.length : 3.14,
		speed: 600,
		infinite: false,
		variableWidth: isSmall,
		arrows: false,
		responsive: [
			{
				breakpoint: MOBILE_MIN_WIDTH,
				settings: {
					slidesToShow: 1.3
				}
			}
		]
	};

	const getPolicyUrlByInsurance = React.useCallback(
		(code: string) => {
			return selectedInsurances.find(selectedInsurance => selectedInsurance.insuranceProgram.code === code)
				?.policyUrl;
		},
		[selectedInsurances]
	);

	const onToggleHandler = (insurance: InsuranceProgramFragment) => () => {
		onToggle(insurance.code);

		const isSelected = selectedInsurances.find(
			selectedInsurance => selectedInsurance.insuranceProgram?.code === insurance.code
		);

		if (isSelected) {
			analytics_removeInsurance(insurance, travellers);
		} else {
			analytics_addInsurance(insurance, travellers);
		}
	};

	const onSaveHandler = () => {
		if (hasChanges) {
			onSave(state);
		} else {
			onClose();
		}
	};
	useEffect(() => {
		WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.InsurancesList, {
			servicesIds: oneInsurance ? [oneInsurance.code] : insurances.map(insurance => insurance.code)
		});
	}, []);

	return (
		<AdditionalServiceWrapper
			className={className}
			headerClassName={css.header}
			textClassName={css.text}
			wrapperClassName={css.wrapper}
			contentClassName={css.content}
			footerClassName={css.footer}
			header={header}
			text={<SafeHtml tag="div" html={description} />}
			onConfirm={onSaveHandler}
			onCancel={onClose}
			isLoading={isLoading}
		>
			{insurances.length > 3 && (
				<div className={css.slider_buttons}>
					<div className={cn(css.slider_button, css.slider_button_prev)} onClick={() => slider.slickPrev()}>
						{Chevron}
					</div>
					<div className={css.slider_button} onClick={() => slider.slickNext()}>
						{Chevron}
					</div>
				</div>
			)}
			{oneInsurance ? (
				<InsuranceCard
					className={css.card}
					insurance={oneInsurance}
					policyUrl={getPolicyUrlByInsurance(oneInsurance.code)}
					isSelected={state.includes(oneInsurance.code)}
					onClick={onToggleHandler(oneInsurance)}
				/>
			) : (
				<Slider ref={setSlider} {...sliderOptions}>
					{insurances.map(insurance => (
						<InsuranceCard
							insurance={insurance}
							policyUrl={getPolicyUrlByInsurance(insurance.code)}
							isSelected={state.includes(insurance.code)}
							onClick={onToggleHandler(insurance)}
						/>
					))}
				</Slider>
			)}
			{totalPrice && (
				<div className={css.total}>
					{t('Total')}:<Money money={totalPrice} />
				</div>
			)}
		</AdditionalServiceWrapper>
	);
};

export default InsurancePopup;
